import { CallDto } from '@act/shared/data-transfer-objects';
import { useCallback, useMemo } from 'react';
import { callsRequests } from 'redux/requests/calls.request';
import { useApiCall } from 'redux/utils/apiHook.hook';
import { useActiveCallConnections } from '../useActiveCallConnections.hook';
import { useActiveCallMenuActions } from '../useActiveCallMenuActions.hook';

/**
 * Hold, end, and leave actions for the minimized call banner.
 *
 * @param call - Active call DTO.
 * @returns Connection state and action handlers for the banner controls.
 */
export function useMinimizedCallActions(call: CallDto) {
  const {
    allConnections,
    guideConnection,
    mainConnection,
    isMainConnectionOnHold,
  } = useActiveCallConnections(call);

  const memberParticipantConnection = useMemo(() => {
    if (!mainConnection) {
      return undefined;
    }

    return allConnections.find(
      connection =>
        !connection.completedAt &&
        (connection.userId === mainConnection.userId ||
          connection.phoneNumber === mainConnection.phoneNumber)
    );
  }, [allConnections, mainConnection]);

  const { handleLeaveCall, isHangingUpGuide } = useActiveCallMenuActions(
    call,
    allConnections,
    guideConnection,
    () => undefined
  );

  const [_holdResult, isTogglingHold, toggleConnectionHold] = useApiCall(
    callsRequests.toggleConnectionHold
  );

  const isConnected = !!guideConnection?.connectedAt;

  const handleHold = useCallback(() => {
    if (!memberParticipantConnection?.id) {
      return;
    }
    toggleConnectionHold({
      body: {
        connectionId: memberParticipantConnection.id,
        onHold: !isMainConnectionOnHold,
      },
    });
  }, [
    memberParticipantConnection?.id,
    toggleConnectionHold,
    isMainConnectionOnHold,
  ]);

  return {
    isConnected,
    isMainConnectionOnHold,
    isHangingUpGuide,
    isTogglingHold,
    handleHold,
    handleLeaveCall,
  };
}
